import type {
	MaskableElement,
	ReplayPrivacySettings,
} from "@weloop/core/feedback/replay-masking";

import {
	DEFAULT_REPLAY_PRIVACY,
	replayMaskingOptions,
} from "@weloop/core/feedback/replay-masking";

import type { UserReplayContext, UserReplaySession } from "./replay-session";
import type { SessionReplayConfig } from "./types";

import {
	beginReplaySession,
	clearReplaySession,
	readReplayEvents,
	readReplaySession,
	storeReplayLeg,
} from "./replay-session";

interface RrwebEvent {
	readonly timestamp: number;
	readonly type: number;
}

// The rrweb UMD self-installs at `window.rrwebRecord`. Only the `record` member is
// used at runtime; keep the typing minimal so the snippet doesn't import any
// rrweb d.ts files into its lean main bundle.
interface RrwebRecordOptions {
	readonly blockClass?: string;
	readonly blockSelector?: string;
	readonly checkoutEveryNms?: number;
	readonly emit?: (event: RrwebEvent, isCheckout?: boolean) => void;
	readonly maskAllInputs?: boolean;
	readonly maskInputFn?: (value: string, element: MaskableElement) => string;
	readonly maskTextClass?: string;
	readonly maskTextSelector?: string;
	readonly recordCanvas?: boolean;
	readonly recordCrossOriginIframes?: boolean;
	readonly slimDOMOptions?: unknown;
}

// rrweb's `record` is callable AND carries static helpers on the same value
// (it's a namespace in the d.ts). We use `takeFullSnapshot` to plant a fresh
// DOM baseline when the user clicks Record – without it, the user-triggered
// stream starts mid-incremental and the replayer has nothing to render onto.
type RrwebRecordFn = (options: RrwebRecordOptions) => (() => void) | undefined;
interface RrwebRecordStatics {
	readonly takeFullSnapshot: () => void;
}

interface RrwebUmd {
	readonly record: RrwebRecordFn & RrwebRecordStatics;
}

declare global {
	interface Window {
		rrwebRecord?: RrwebUmd;
	}
}

/** The rolling buffer frozen at one moment, compressed only when asked. */
export interface ReplaySnapshot {
	readonly encode: () => Promise<Uint8Array>;
	readonly windowMs: number;
}

export interface SessionReplayOptions {
	// Called once a user recording holds `MAX_USER_EVENTS` events on one page.
	// The recording stops buffering; the caller is expected to stop it.
	readonly onUserRecordingFull?: () => void;
	// The project this page mounted the widget for. A recording resumed from
	// an earlier page of another project is discarded.
	readonly projectId?: string;
}

// The most events one page load's leg of a user recording keeps in memory.
// Every event stays until Stop, then the whole recording is serialized at
// once, so an unbounded leg would grow the host page's memory without limit.
const MAX_USER_EVENTS = 20_000;

/**
 * Host-page rrweb recorder for the rolling buffer and user-triggered
 * recordings.
 *
 * The rolling buffer (silent capture of the last seconds before a feedback)
 * only runs when the project opted in – `config` is defined. User-triggered
 * recordings work either way: without the buffer, the rrweb bundle loads
 * lazily when a recording starts and the listener is torn down again when it
 * ends, so an opted-out host page never records outside an explicit ask.
 *
 * The recorder is bootstrapped from the snippet (the host page) – never from
 * the widget iframe, because the iframe sees only its own DOM. Events live
 * entirely in memory until the user submits feedback; nothing crosses the
 * network unless `snapshot()` is called.
 *
 * The "rolling buffer" trick is rrweb's checkout pattern: the recorder
 * pushes events into the tail of a two-array matrix, and on every checkout
 * (`checkoutEveryNms`) it starts a new tail. We always concatenate the
 * second-to-last and last arrays to dump – which means the effective
 * coverage sits between N and 2N. Acceptable for "what happened just before
 * the user opened the widget".
 *
 * Masking follows `replayMaskingOptions` from `@weloop/core`. The rolling
 * buffer always records with the default (every input masked). A user
 * recording asked for by a business rule records with that rule's privacy
 * mode and sensitive selectors instead: rrweb applies masking when it
 * serializes a node, so the listener restarts with the rule's options and
 * goes back to the default once the recording ends.
 */
export interface SessionReplayRecorder {
	/**
	 * The recording in progress, including one resumed from a previous page
	 * load in the same tab. `undefined` when nothing is being recorded.
	 */
	readonly activeUserRecording: () => UserReplaySession | undefined;
	/** Stop recording and release the rrweb listener. */
	readonly dispose: () => void;
	/**
	 * Freeze the current rolling window plus the duration the events cover
	 * (best-effort, since events arrive asynchronously from rrweb). Copying
	 * is cheap; the gzip runs only when `encode` is called, since most panel
	 * opens never submit a feedback. The recorder keeps running afterwards –
	 * a single page lifetime can submit multiple feedbacks.
	 */
	readonly snapshot: () => ReplaySnapshot | undefined;
	/**
	 * Begin capturing a user-triggered recording. Plants a fresh full
	 * snapshot via rrweb so the resulting stream is replay-ready, then
	 * starts mirroring every event into a parallel sink alongside the
	 * rolling buffer. Idempotent: a second call before `stopUserRecording`
	 * resets the sink.
	 *
	 * The recording is registered in session storage so it survives host-page
	 * navigations: the next page load resumes it instead of starting over.
	 *
	 * Resolves to `false` when the rrweb listener could not start (the
	 * bundle failed to load), so the caller can end the recording.
	 */
	readonly startUserRecording: (
		context: UserReplayContext | undefined,
		privacy: ReplayPrivacySettings,
	) => Promise<boolean>;
	/**
	 * Finalize the user-triggered recording, returning the gzipped events
	 * and the wall-clock duration between the start call and now (or
	 * `undefined` if there was no active recording or the sink is empty).
	 * Tearing the sink down on stop means the rolling buffer keeps running
	 * untouched – the user can record multiple times in one page lifetime.
	 */
	readonly stopUserRecording: () => Promise<
		{ durationMs: number; events: Uint8Array } | undefined
	>;
}

const gzipJson = async (value: unknown): Promise<Uint8Array> => {
	const json = JSON.stringify(value);
	const stream = new Blob([json], { type: "application/json" })
		.stream()
		.pipeThrough(new CompressionStream("gzip"));
	const chunks: Uint8Array[] = [];
	const reader = stream.getReader();
	while (true) {
		const { done, value: chunk } = await reader.read();
		if (done) {
			break;
		}
		if (chunk !== undefined) {
			chunks.push(chunk);
		}
	}
	const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
	const merged = new Uint8Array(totalLength);
	let offset = 0;
	for (const chunk of chunks) {
		merged.set(chunk, offset);
		offset += chunk.length;
	}
	return merged;
};

// Loads the rrweb UMD into the host page. The same script src across calls
// resolves to the same global, so we share a single load promise – multiple
// replay starts during one page lifetime do not re-fetch the bundle.
let rrwebLoaderPromise: Promise<RrwebUmd> | undefined;

async function loadRrwebUmd(scriptUrl: string): Promise<RrwebUmd> {
	if (rrwebLoaderPromise !== undefined) {
		return await rrwebLoaderPromise;
	}
	if (window.rrwebRecord !== undefined) {
		rrwebLoaderPromise = Promise.resolve(window.rrwebRecord);
		return await rrwebLoaderPromise;
	}
	const script = document.createElement("script");
	// oxlint-disable-next-line promise/avoid-new -- no DOM helper exists for awaiting a <script> tag load
	const loading = new Promise<RrwebUmd>((resolve, reject) => {
		script.src = scriptUrl;
		script.async = true;
		script.crossOrigin = "anonymous";
		script.addEventListener("load", () => {
			if (window.rrwebRecord === undefined) {
				reject(new Error("rrweb global missing after script load"));
				return;
			}
			resolve(window.rrwebRecord);
		});
		script.addEventListener("error", () => {
			reject(new Error(`Failed to load rrweb bundle from ${scriptUrl}`));
		});
		document.head.append(script);
	});
	// A failed load must not stick for the page lifetime: forget it and drop
	// the dead tag so the next recording fetches the bundle again.
	rrwebLoaderPromise = (async () => {
		try {
			return await loading;
		} catch (error) {
			rrwebLoaderPromise = undefined;
			script.remove();
			throw error;
		}
	})();
	return await rrwebLoaderPromise;
}

// Session storage is unreachable on some host pages (sandboxed iframes,
// blocked third-party storage). Resolving it once keeps every call site free
// of the try/catch – a recording then simply stays single-page.
// A selector the browser cannot parse throws here. `replayMaskingOptions`
// drops it so one typo does not switch off every other selector.
const isValidSelector = (selector: string): boolean => {
	try {
		document.createDocumentFragment().querySelector(selector);
		return true;
	} catch {
		return false;
	}
};

const isSamePrivacy = (
	left: ReplayPrivacySettings,
	right: ReplayPrivacySettings,
): boolean =>
	left.privacyMode === right.privacyMode &&
	left.sensitiveSelectors.join("\n") === right.sensitiveSelectors.join("\n");

const resolveStorage = (): Storage | undefined => {
	try {
		return window.sessionStorage;
	} catch {
		return undefined;
	}
};

export const startSessionReplay = (
	config: SessionReplayConfig | undefined,
	replayBundleUrl: string,
	options: SessionReplayOptions = {},
): SessionReplayRecorder => {
	// Two-array matrix per the rrweb rolling-buffer recipe. The recorder
	// pushes into the tail; a new tail starts on every checkout.
	let matrix: RrwebEvent[][] = [[]];
	let stop: (() => void) | undefined;
	let disposed = false;

	// Parallel sink for the user-triggered recording. While defined, every
	// event the recorder emits is also pushed here. Closing the sink (set
	// back to `undefined`) leaves the rolling matrix untouched.
	let userSink: RrwebEvent[] | undefined;
	// Storage key of this page load's leg, once a flush stored it. Later
	// flushes replace that leg instead of adding a second one.
	let pageKey: string | undefined;
	let rrwebInstance: RrwebUmd | undefined;

	const storage = resolveStorage();

	// A recording started on an earlier page of this tab. Reopening the sink
	// before rrweb loads means the new page's own meta and full-snapshot
	// events land in it, so each leg of the recording is replay-ready on its
	// own.
	let session = storage === undefined ? undefined : readReplaySession(storage);
	if (session !== undefined && session.projectId !== options.projectId) {
		if (storage !== undefined) {
			clearReplaySession(storage);
		}
		session = undefined;
	}
	if (session !== undefined) {
		userSink = [];
	}
	// The masking the rrweb listener records with. A resumed recording keeps
	// the settings it started with on the first page.
	let recorderPrivacy = session?.privacy ?? DEFAULT_REPLAY_PRIVACY;

	// Hand the current leg over before the page goes away. `pagehide` is the
	// one unload signal that fires for every navigation kind, including the
	// back/forward cache; `visibilitychange` covers the tab being hidden,
	// where a browser may discard the page without another chance to write.
	const flush = (): void => {
		if (
			storage === undefined ||
			userSink === undefined ||
			session === undefined
		) {
			return;
		}
		// The sink keeps every event of this page load: the stored leg is a
		// copy, rewritten whole on each flush, so a hidden tab that comes back
		// keeps one leg that opens with its full snapshot. A failed write
		// leaves the events in memory instead of losing them.
		pageKey = storeReplayLeg(storage, userSink, pageKey) ?? pageKey;
		session = readReplaySession(storage) ?? session;
	};

	window.addEventListener("pagehide", flush);
	const onVisibilityChange = (): void => {
		if (document.visibilityState === "hidden") {
			flush();
		}
	};
	document.addEventListener("visibilitychange", onVisibilityChange);

	// Starts the rrweb listener once; concurrent calls share the promise. Set
	// back to `undefined` when the listener is torn down (dispose, or a
	// user-only recording ending) so a later recording can bring it back. The
	// generation counter invalidates a start still waiting on the rrweb load
	// when a teardown-and-restart happens underneath it.
	// Resolves to whether the listener runs – `false` only when it failed to
	// start, since a superseded start is not a failure.
	let recorderPromise: Promise<boolean> | undefined;
	let recorderGeneration = 0;

	const startRecorder = async (): Promise<boolean> => {
		if (recorderPromise !== undefined) {
			return await recorderPromise;
		}
		recorderGeneration += 1;
		const generation = recorderGeneration;
		recorderPromise = (async () => {
			try {
				const rrweb = await loadRrwebUmd(replayBundleUrl);
				// A dispose or an ended user-only recording tore this start down
				// while rrweb was still loading – do not begin listening now.
				if (
					disposed ||
					generation !== recorderGeneration ||
					recorderPromise === undefined
				) {
					return true;
				}
				rrwebInstance = rrweb;
				const handler = rrweb.record({
					// Read at listener start, so a restart picks up new settings.
					...replayMaskingOptions(recorderPrivacy, isValidSelector),
					blockClass: "weloop-block",
					blockSelector: "[data-weloop-block]",
					// Checkouts drive the rolling buffer only – a user-triggered
					// recording keeps every event from its start.
					...(config === undefined
						? {}
						: { checkoutEveryNms: config.durationSeconds * 1000 }),
					emit: (event, isCheckout) => {
						if (config !== undefined) {
							if (isCheckout === true) {
								matrix.push([]);
								// Cap the matrix at three buckets: the active tail plus
								// the one we'll concatenate on snapshot. Anything older
								// drops.
								if (matrix.length > 3) {
									matrix = matrix.slice(-3);
								}
							}
							matrix.at(-1)?.push(event);
						}
						if (userSink === undefined || userSink.length >= MAX_USER_EVENTS) {
							return;
						}
						userSink.push(event);
						if (userSink.length === MAX_USER_EVENTS) {
							// Outside rrweb's emit: the stop tears the listener down.
							queueMicrotask(() => options.onUserRecordingFull?.());
						}
					},
					recordCanvas: false,
					// Cross-origin iframes (the widget itself, embedded analytics, …)
					// are skipped – we are deliberately not crossing the boundary.
					recordCrossOriginIframes: false,
					slimDOMOptions: {
						comment: true,
						headFavicon: true,
						headMetaAuthorship: true,
						headMetaDescKeywords: true,
						headMetaHttpEquiv: true,
						headMetaRobots: true,
						headMetaSocial: true,
						headMetaVerification: true,
						headWhitespace: true,
						script: true,
					},
				});
				stop = handler ?? undefined;
				return true;
			} catch (error) {
				console.error("[weloop] session replay failed to start", error);
				// Allow a later user recording to retry the load.
				if (generation === recorderGeneration) {
					recorderPromise = undefined;
				}
				return false;
			}
		})();
		return await recorderPromise;
	};

	// Stop the rrweb listener so the next `startRecorder` builds a new one.
	const tearDownRecorder = (): void => {
		stop?.();
		stop = undefined;
		recorderPromise = undefined;
	};

	// The rolling buffer records from page load. Without it, rrweb loads only
	// when a user recording starts – or resumes from a previous page.
	if (config !== undefined || session !== undefined) {
		void startRecorder();
	}

	return {
		activeUserRecording: () => session,
		dispose: () => {
			// Hand the current leg over first: `dispose` also runs before a
			// remount, which picks the recording up when it is for the same
			// project.
			flush();
			disposed = true;
			tearDownRecorder();
			window.removeEventListener("pagehide", flush);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			matrix = [[]];
			userSink = undefined;
			rrwebInstance = undefined;
		},
		snapshot: () => {
			// While a rule's recording runs, the buffer holds events recorded
			// with that rule's masking. The panel reopens before the recording
			// stops and drops them, so a snapshot taken then would carry them
			// into a later feedback.
			if (!isSamePrivacy(recorderPrivacy, DEFAULT_REPLAY_PRIVACY)) {
				return undefined;
			}
			const buckets = matrix;
			const tailIndex = buckets.length - 1;
			if (tailIndex < 0) {
				return undefined;
			}
			const tail = buckets[tailIndex] ?? [];
			const previous = tailIndex > 0 ? (buckets[tailIndex - 1] ?? []) : [];
			const events = [...previous, ...tail];
			if (events.length === 0) {
				return undefined;
			}
			const first = events[0]?.timestamp ?? 0;
			const last = events.at(-1)?.timestamp ?? 0;
			const windowMs = Math.max(0, last - first);
			let encoded: Promise<Uint8Array> | undefined;
			return {
				encode: async () => await (encoded ??= gzipJson(events)),
				windowMs,
			};
		},
		startUserRecording: async (context, privacy) => {
			const wasRecording = stop !== undefined;
			// rrweb masks when it serializes, so a listener running with other
			// settings cannot be reused: restart it, and the fresh listener emits
			// its own full snapshot with the rule's masking applied.
			const isRestartNeeded =
				wasRecording && !isSamePrivacy(recorderPrivacy, privacy);
			recorderPrivacy = privacy;
			userSink = [];
			pageKey = undefined;
			const startedAt = Date.now();
			const opened = {
				context,
				id: String(startedAt),
				privacy,
				projectId: options.projectId,
				startedAt,
			};
			session =
				storage === undefined
					? { ...opened, chunkKeys: [], nextChunk: 0 }
					: beginReplaySession(storage, opened);
			if (isRestartNeeded) {
				tearDownRecorder();
			}
			return await (async () => {
				const isListening = await startRecorder();
				// An already-running recorder only emits increments, so plant a
				// full DOM baseline for the new sink to make the captured stream
				// replay-ready. A freshly started rrweb emits its own.
				if (isListening && wasRecording && !isRestartNeeded) {
					rrwebInstance?.record.takeFullSnapshot();
				}
				return isListening;
			})();
		},
		stopUserRecording: async () => {
			const startedAt = session?.startedAt;
			const stored =
				storage === undefined ? [] : readReplayEvents(storage, pageKey);
			const tail = userSink ?? [];
			userSink = undefined;
			pageKey = undefined;
			session = undefined;
			if (storage !== undefined) {
				clearReplaySession(storage);
			}
			// Without the rolling buffer the project has not opted into silent
			// recording – tear the listener down once the explicit capture ends.
			// With it, the buffer goes back to the default masking. Its events
			// recorded under the rule's settings are dropped, since a looser
			// rule must not leak into a later feedback's buffer snapshot.
			if (config === undefined) {
				tearDownRecorder();
				recorderPrivacy = DEFAULT_REPLAY_PRIVACY;
			} else if (!isSamePrivacy(recorderPrivacy, DEFAULT_REPLAY_PRIVACY)) {
				tearDownRecorder();
				recorderPrivacy = DEFAULT_REPLAY_PRIVACY;
				matrix = [[]];
				void startRecorder();
			}
			// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- stored events round-tripped through JSON keep the shape rrweb emitted
			const events = [...(stored as RrwebEvent[]), ...tail];
			if (events.length === 0 || startedAt === undefined) {
				return undefined;
			}
			const durationMs = Math.max(0, Date.now() - startedAt);
			const gzipped = await gzipJson(events);
			return { durationMs, events: gzipped };
		},
	};
};
